// First, add these imports at the top of the file
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  pdf,
  Font,
  Image,
} from "@react-pdf/renderer";
import { saveAs } from "file-saver";
import Logo from "../img/logo.png";

const months = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

// Define the PDF styles
const pdfStyles = StyleSheet.create({
  page: {
    flexDirection: "column",
    padding: 30,
    fontFamily: "Helvetica",
  },
  header: {
    flexDirection: "row",
    marginBottom: 20,
    justifyContent: "space-between",
  },
  headerContent: {
    flex: 1,
  },
  logo: {
    width: 100,
    height: 50,
  },
  title: {
    fontSize: 18,
    fontWeight: "bold",
    marginBottom: 10,
  },
  patientName: {
    fontSize: 14,
    marginBottom: 5,
  },
  infoRow: {
    flexDirection: "row",
    marginBottom: 5,
  },
  infoLabel: {
    fontSize: 10,
    color: "#555555",
    width: 80,
  },
  infoValue: {
    fontSize: 10,
    marginRight: 20,
  },
  section: {
    marginTop: 20,
    borderBottom: 1,
    borderBottomColor: "#e0e0e0",
    paddingBottom: 10,
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: "bold",
    marginBottom: 10,
  },
  subSection: {
    marginTop: 10,
    marginBottom: 5,
  },
  subSectionTitle: {
    fontSize: 12,
    fontWeight: "bold",
    marginBottom: 5,
  },
  item: {
    marginBottom: 5,
    flexDirection: "row",
  },
  checkboxLabel: {
    fontSize: 10,
    marginLeft: 5,
  },
  checkbox: {
    width: 10,
    height: 10,
    borderRadius: 2,
    borderWidth: 1,
    borderColor: "#000",
    marginRight: 5,
  },
  checkedBox: {
    backgroundColor: "#000",
  },
  label: {
    fontSize: 10,
    fontWeight: "bold",
    marginRight: 5,
  },
  text: {
    fontSize: 10,
    marginBottom: 5,
  },
  footer: {
    position: "absolute",
    bottom: 30,
    left: 30,
    right: 30,
    fontSize: 8,
    textAlign: "center",
    color: "grey",
  },
  logo: {
    width: 100,
    height: 50,
    marginLeft: 20,
    objectFit: "contain",
  },
  addressText: {
    fontSize: 9,
    color: "#444444",
    marginBottom: 2,
  },
});

// Create the PDF Document component
const TreatmentNotesPDF = ({ data, visit, address }) => {
  // Helper function to check if any field in a section has content
  const hasContent = (obj, fields) => {
    if (!obj) return false;
    return fields.some((field) => {
      const value = obj[field];
      if (typeof value === "boolean") return value;
      return value && String(value).trim() !== "";
    });
  };

  // Helper function to format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return `${
      months[date.getMonth()]
    } ${date.getDate()}, ${date.getFullYear()}`;
  };

  // Helper function to format time
  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  // Get a section by its ID from the sections array
  const getSection = (data, sectionId) => {
    if (!data || !data.sections) return null;
    return data.sections.find(
      (section) => section.id === sectionId && section.isSelected
    );
  };

  // Get a question from a section by its ID
  const getQuestion = (section, questionId) => {
    if (!section || !section.questions) return null;
    return section.questions.find((question) => question.id === questionId);
  };

  // Get all checked checkboxes from a checkbox group question
  const getCheckedItems = (question) => {
    if (!question || !question.values) return [];
    return question.values.filter((item) => item.isChecked);
  };

  return (
    <Document>
      <Page size="A4" style={pdfStyles.page}>
        {/* Header */}
        <View style={pdfStyles.header}>
          <View style={pdfStyles.headerContent}>
            <Text style={pdfStyles.title}>Treatment Notes</Text>
            <Text style={pdfStyles.patientName}>{visit.patient.name}</Text>
            {/* Add patient address here */}
            {address && (
              <View style={{ marginBottom: 8 }}>
                <Text style={pdfStyles.addressText}>
                  {address.addressStreet || "Address not provided"}
                </Text>
                <Text style={pdfStyles.addressText}>
                  {address.addressCity || ""}{" "}
                </Text>
                <Text style={pdfStyles.addressText}>
                  {address.addressPostal || ""}
                </Text>
              </View>
            )}
            <View style={pdfStyles.infoRow}>
              <Text style={pdfStyles.infoLabel}>DATE:</Text>
              <Text style={pdfStyles.infoValue}>
                {formatDate(visit.date.start)}
              </Text>
            </View>
            <View style={pdfStyles.infoRow}>
              <Text style={pdfStyles.infoLabel}>TIME:</Text>
              <Text style={pdfStyles.infoValue}>
                {formatTime(visit.date.start)} - {formatTime(visit.date.end)}
              </Text>
            </View>
            <View style={pdfStyles.infoRow}>
              <Text style={pdfStyles.infoLabel}>PRACTITIONER:</Text>
              <Text style={pdfStyles.infoValue}>
                {visit.service[0].practitioner.name}
              </Text>
            </View>
            <View style={pdfStyles.infoRow}>
              <Text style={pdfStyles.infoLabel}>SERVICE:</Text>
              <Text style={pdfStyles.infoValue}>{visit.service[0].type}</Text>
            </View>
          </View>
          <Image src={Logo} style={pdfStyles.logo} />
        </View>

        {/* Duration */}
        {data.metadata && data.metadata.duration && (
          <View style={pdfStyles.section}>
            <Text style={pdfStyles.label}>
              Treatment/Assessment Duration (Mins):
            </Text>
            <Text style={pdfStyles.text}>{data.metadata.duration}</Text>
          </View>
        )}

        {/* Dynamically render each active section */}
        {data.sections &&
          data.sections
            .filter((section) => section.isSelected)
            .map((section) => (
              <View key={section.id} style={pdfStyles.section}>
                <Text style={pdfStyles.sectionTitle}>{section.name}</Text>

                {/* Render each question in the section */}
                {section.questions.map((question) => {
                  // Handle different question types
                  switch (question.type) {
                    case "checkboxGroup":
                      return (
                        <View key={question.id} style={pdfStyles.subSection}>
                          <Text style={pdfStyles.subSectionTitle}>
                            {question.name}
                          </Text>
                          {question.values
                            .filter((item) => item.isChecked)
                            .map((item) => (
                              <View key={item.id} style={pdfStyles.item}>
                                <View
                                  style={[
                                    pdfStyles.checkbox,
                                    pdfStyles.checkedBox,
                                  ]}
                                />
                                <Text style={pdfStyles.checkboxLabel}>
                                  {item.label}
                                </Text>
                              </View>
                            ))}
                        </View>
                      );

                    case "text":
                      if (!question.value) return null;
                      return (
                        <View key={question.id} style={pdfStyles.subSection}>
                          <Text style={pdfStyles.label}>{question.name}:</Text>
                          <Text style={pdfStyles.text}>{question.value}</Text>
                        </View>
                      );

                    case "textArea":
                      if (!question.value) return null;
                      return (
                        <View key={question.id} style={pdfStyles.subSection}>
                          <Text style={pdfStyles.subSectionTitle}>
                            {question.name}
                          </Text>
                          {question.description && (
                            <Text
                              style={[
                                pdfStyles.text,
                                { fontStyle: "italic", marginBottom: 5 },
                              ]}
                            >
                              {question.description}
                            </Text>
                          )}
                          <Text style={pdfStyles.text}>{question.value}</Text>
                        </View>
                      );

                    case "group":
                      return (
                        <View key={question.id} style={pdfStyles.subSection}>
                          <Text style={pdfStyles.subSectionTitle}>
                            {question.name}
                          </Text>
                          {question.questions &&
                            question.questions.map((subQuestion) => (
                              <View key={subQuestion.id} style={pdfStyles.item}>
                                <Text style={pdfStyles.label}>
                                  {subQuestion.name}:
                                </Text>
                                <Text style={pdfStyles.text}>
                                  {subQuestion.value || "N/A"}
                                </Text>
                              </View>
                            ))}
                        </View>
                      );

                    case "table":
                      if (!question.value || question.value.length === 0)
                        return null;
                      return (
                        <View key={question.id} style={pdfStyles.subSection}>
                          <Text style={pdfStyles.subSectionTitle}>
                            {question.name}
                          </Text>
                          <View
                            style={{
                              flexDirection: "row",
                              marginBottom: 5,
                              borderBottomWidth: 1,
                              borderColor: "#ddd",
                              paddingBottom: 2,
                            }}
                          >
                            {question.columns &&
                              question.columns.map((column, idx) => (
                                <Text
                                  key={idx}
                                  style={{
                                    flex: 1,
                                    fontSize: 9,
                                    fontWeight: "bold",
                                  }}
                                >
                                  {column}
                                </Text>
                              ))}
                          </View>
                          {question.value
                            .filter(
                              (row) =>
                                row.supplement || row.dose || row.instructions
                            )
                            .map((row, idx) => (
                              <View
                                key={idx}
                                style={{
                                  flexDirection: "row",
                                  marginBottom: 3,
                                }}
                              >
                                <Text style={{ flex: 1, fontSize: 9 }}>
                                  {row.supplement || "N/A"}
                                </Text>
                                <Text style={{ flex: 1, fontSize: 9 }}>
                                  {row.dose || "N/A"}
                                </Text>
                                <Text style={{ flex: 1, fontSize: 9 }}>
                                  {row.instructions || "N/A"}
                                </Text>
                              </View>
                            ))}
                        </View>
                      );

                    default:
                      return null;
                  }
                })}
              </View>
            ))}

        {/* Footer */}
        <Text style={pdfStyles.footer}>
          Appleby Wellness Treatment Notes - Generated on{" "}
          {new Date().toLocaleString()}
        </Text>
      </Page>
    </Document>
  );
};

// Export function to generate PDF
export const generatePdf = async (treatmentData, visit, address) => {
  try {
    const blob = await pdf(
      <TreatmentNotesPDF data={treatmentData} visit={visit} address={address} />
    ).toBlob();

    const patientName = visit.patient.name.replace(/\s+/g, "_");
    const dateStr = new Date(visit.date.start).toISOString().split("T")[0];
    const fileName = `Treatment_Notes_${patientName}_${dateStr}.pdf`;

    saveAs(blob, fileName);
  } catch (error) {
    console.error("Error generating PDF:", error);
  }
};

export default TreatmentNotesPDF;
